/**
 * Find a time: how free times are found, ranked and explained, and how a rhythm's dates
 * are laid out and checked.
 *
 * Ranking follows the brief: free for the whole length, a 15-minute buffer after busy
 * blocks, the time of day the person picked, and when they finished steps before. The
 * person always decides — these only order and explain the options.
 *
 * Pure (no imports beyond ./calendar.ts) so it runs under `node --test`.
 */

import {
  CHECK_WEEKS,
  DAY_END_MIN,
  DAY_START_MIN,
  addDays,
  dayKey,
  formatClock,
  formatTime,
  minutesOf,
  parseDayKey,
  partOfDay,
  sameDay,
  startOfDay,
  weekdayLong,
  type IfBusy,
  type Rhythm,
  type TimeOfDay,
  type TimeRange,
  type Weekday,
} from "./calendar.ts";

/** Minutes after local midnight, half-open [start, end). */
export interface Interval {
  start: number;
  end: number;
}

/** When this person tends to finish steps, from the ones they've completed. */
export interface History {
  part: Exclude<TimeOfDay, "any"> | null;
  /** How many recent completed steps fell in `part`. */
  count: number;
  /** Their usual start time within `part`. */
  typicalMin: number | null;
}

export const NO_HISTORY: History = { part: null, count: 0, typicalMin: null };

export interface Slot {
  /** `yyyy-MM-dd` — usually the picked day, but a full day offers its neighbours. */
  day: string;
  start: number;
  end: number;
  reason: string;
  tag?: "best" | "overlaps";
}

export const BUFFER_MIN = 15;
const STEP_MIN = 15;
const ANCHOR: Record<TimeOfDay, number> = { morning: 8 * 60, afternoon: 13 * 60, evening: 18 * 60 + 30, any: 9 * 60 };

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ── Busy intervals ───────────────────────────────────────────────────────────

/** The busy ranges that touch `day`, clipped to it, merged and sorted. */
export function busyOnDay(busy: TimeRange[], day: Date): Interval[] {
  const dayStart = startOfDay(day);
  const dayEnd = addDays(dayStart, 1);
  const out: Interval[] = [];
  for (const r of busy) {
    const s = new Date(r.start);
    const e = new Date(r.end);
    if (!(e > dayStart && s < dayEnd)) continue;
    out.push({
      start: s <= dayStart ? 0 : minutesOf(s),
      end: e >= dayEnd ? 24 * 60 : minutesOf(e),
    });
  }
  out.sort((a, b) => a.start - b.start);
  const merged: Interval[] = [];
  for (const iv of out) {
    const last = merged[merged.length - 1];
    if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end);
    else merged.push({ ...iv });
  }
  return merged;
}

export function overlapping(iv: Interval, busy: Interval[]): Interval | null {
  return busy.find((b) => b.start < iv.end && iv.start < b.end) ?? null;
}

function overlapMinutes(iv: Interval, busy: Interval[]): number {
  return busy.reduce((sum, b) => sum + Math.max(0, Math.min(iv.end, b.end) - Math.max(iv.start, b.start)), 0);
}

function prevBusyEnd(start: number, busy: Interval[]): number | null {
  let end: number | null = null;
  for (const b of busy) if (b.end <= start) end = b.end;
  return end;
}

function nextBusyStart(end: number, busy: Interval[]): number | null {
  return busy.find((b) => b.start >= end)?.start ?? null;
}

/** Free minutes inside the 7 AM – 9 PM window. */
export function freeMinutes(busy: Interval[]): number {
  const window = DAY_END_MIN - DAY_START_MIN;
  return window - overlapMinutes({ start: DAY_START_MIN, end: DAY_END_MIN }, busy);
}

/**
 * The month's dots: 0 when no gap fits, up to 3 for a day with lots of room. `fromMin`
 * is where the day starts counting — later than 7 AM for today, once time has passed.
 */
export function freeDots(busy: Interval[], durationMin: number, fromMin: number = DAY_START_MIN): 0 | 1 | 2 | 3 {
  let longest = 0;
  let cursor = fromMin;
  for (const b of [...busy, { start: DAY_END_MIN, end: DAY_END_MIN }]) {
    if (b.end <= fromMin) continue;
    longest = Math.max(longest, Math.min(b.start, DAY_END_MIN) - cursor);
    cursor = Math.max(cursor, b.end);
  }
  if (longest < durationMin) return 0;
  const free = DAY_END_MIN - fromMin - overlapMinutes({ start: fromMin, end: DAY_END_MIN }, busy);
  if (free >= 6 * 60) return 3;
  if (free >= 3 * 60) return 2;
  return 1;
}

// ── Single slots ─────────────────────────────────────────────────────────────

/** First start a step can have on `day`: 7 AM, or the next quarter hour if it's today. */
export function firstStart(day: Date, now: Date): number {
  if (!sameDay(day, now)) return DAY_START_MIN;
  return Math.max(DAY_START_MIN, Math.ceil((minutesOf(now) + 1) / STEP_MIN) * STEP_MIN);
}

/** Every quarter-hour start on `day` that's free for the whole length. */
export function freeStarts(day: Date, durationMin: number, busy: Interval[], now: Date): number[] {
  const out: number[] = [];
  for (let s = firstStart(day, now); s + durationMin <= DAY_END_MIN; s += STEP_MIN) {
    if (!overlapping({ start: s, end: s + durationMin }, busy)) out.push(s);
  }
  return out;
}

function score(start: number, durationMin: number, busy: Interval[], anchor: number, preferBonus: boolean): number {
  let sc = preferBonus ? 60 : 0;
  sc -= Math.abs(start - anchor) / 15;
  const prev = prevBusyEnd(start, busy);
  if (prev !== null && start - prev < BUFFER_MIN) sc -= 25;
  const next = nextBusyStart(start + durationMin, busy);
  sc += Math.min(next === null ? 60 : next - (start + durationMin), 60) / 10;
  if (start % 30 === 0) sc += 2;
  return sc;
}

function preferredAnchor(prefer: TimeOfDay, history: History): number {
  if (history.typicalMin !== null && (prefer === "any" || history.part === prefer)) return history.typicalMin;
  return ANCHOR[prefer];
}

function bestStart(
  starts: number[],
  durationMin: number,
  busy: Interval[],
  prefer: TimeOfDay,
  history: History,
): number | null {
  const anchor = preferredAnchor(prefer, history);
  let best: number | null = null;
  let bestScore = -Infinity;
  for (const s of starts) {
    const sc = score(s, durationMin, busy, anchor, prefer !== "any" && partOfDay(s) === prefer);
    if (sc > bestScore) {
      bestScore = sc;
      best = s;
    }
  }
  return best;
}

function historyNote(part: Exclude<TimeOfDay, "any">, prefer: TimeOfDay, history: History): string {
  if (history.part === part && history.count >= 2) return ` ${cap(part)}, like your last ${history.count} completed steps.`;
  if (prefer === part) return ` ${cap(part)}, the time of day you picked.`;
  return "";
}

function freeUntil(end: number, busy: Interval[]): string {
  const next = nextBusyStart(end, busy);
  return next === null ? "Free the rest of the day." : `Free until ${formatClock(next)}.`;
}

function bestReason(start: number, durationMin: number, busy: Interval[], prefer: TimeOfDay, history: History): string {
  return `${freeUntil(start + durationMin, busy)}${historyNote(partOfDay(start), prefer, history)}`;
}

function altReason(start: number, durationMin: number, busy: Interval[], best: number): string {
  const end = start + durationMin;
  const prev = prevBusyEnd(start, busy);
  const next = nextBusyStart(end, busy);
  if (prev !== null && start - prev === BUFFER_MIN) return "Free. 15 minutes after a busy block ends.";
  if (partOfDay(start) === partOfDay(best)) {
    if (start < best) return next === null ? "Free. An earlier start." : `Free. Earlier start, more buffer before ${formatClock(next)}.`;
    return next === null ? "Free. A later start." : `Free until ${formatClock(next)}. A later start.`;
  }
  if (next === null) return `Free. Your ${partOfDay(start)} is open.`;
  if (next - end === BUFFER_MIN) return `Free, with 15 minutes before a busy block at ${formatClock(next)}.`;
  return `Free until ${formatClock(next)}.`;
}

/** Why a time the person typed themselves works (or doesn't). */
export function ownTimeReason(start: number, durationMin: number, busy: Interval[]): string {
  const clash = overlapping({ start, end: start + durationMin }, busy);
  if (clash) return `Overlaps a busy time, ${formatTime(clash.start)} – ${formatTime(clash.end)}.`;
  if (start < 8 * 60) return "Free. First thing, before the day gets going.";
  return freeUntil(start + durationMin, busy);
}

export interface SuggestInput {
  day: Date;
  durationMin: number;
  busy: Interval[];
  prefer: TimeOfDay;
  history: History;
  now: Date;
  /** Busy for neighbouring days, used only when `day` is full. */
  busyFor?: (day: Date) => Interval[] | null;
}

export interface Suggestions {
  /** True when no gap on the day fits the length; `slots` then offers the nearest times. */
  full: boolean;
  slots: Slot[];
}

/**
 * The free times for a day: the best fit first, then up to three others — one more in
 * the same part of the day, then the best of each other part — so the list spans the day.
 */
export function suggestSlots(input: SuggestInput): Suggestions {
  const { day, durationMin, busy, prefer, history, now } = input;
  const starts = freeStarts(day, durationMin, busy, now);
  if (starts.length === 0) return { full: true, slots: nearestWhenFull(input) };

  const best = bestStart(starts, durationMin, busy, prefer, history)!;
  const key = dayKey(day);
  const slots: Slot[] = [
    { day: key, start: best, end: best + durationMin, tag: "best", reason: bestReason(best, durationMin, busy, prefer, history) },
  ];
  const clear = (s: number) => slots.every((x) => Math.abs(x.start - s) >= Math.max(durationMin, 30));

  const samePart = starts.filter((s) => partOfDay(s) === partOfDay(best) && clear(s));
  const twin = bestStart(samePart, durationMin, busy, prefer, history);
  if (twin !== null) slots.push({ day: key, start: twin, end: twin + durationMin, reason: altReason(twin, durationMin, busy, best) });

  for (const part of ["morning", "afternoon", "evening"] as const) {
    if (slots.length >= 4 || part === partOfDay(best)) continue;
    const inPart = starts.filter((s) => partOfDay(s) === part && clear(s));
    const pick = bestStart(inPart, durationMin, busy, part, NO_HISTORY);
    if (pick !== null) slots.push({ day: key, start: pick, end: pick + durationMin, reason: altReason(pick, durationMin, busy, best) });
  }
  return { full: false, slots };
}

function nearestWhenFull({ day, durationMin, busy, prefer, history, now, busyFor }: SuggestInput): Slot[] {
  const out: Slot[] = [];
  const today = startOfDay(now);

  for (let i = 1; i <= 7 && busyFor; i++) {
    const next = addDays(day, i);
    const nb = busyFor(next);
    if (!nb) break;
    const s = bestStart(freeStarts(next, durationMin, nb, now), durationMin, nb, prefer, history);
    if (s === null) continue;
    const lead = i === 1 ? "The next day." : `${weekdayLong(next.getDay())}, ${i} days later.`;
    const tail = bestReason(s, durationMin, nb, prefer, history).replace(/\.\s(\w)/, (_m, c: string) => `, ${c.toLowerCase()}`);
    out.push({ day: dayKey(next), start: s, end: s + durationMin, tag: "best", reason: `${lead} ${tail}` });
    break;
  }

  const prev = addDays(day, -1);
  const pb = prev >= today && busyFor ? busyFor(prev) : null;
  if (pb) {
    const s = bestStart(freeStarts(prev, durationMin, pb, now), durationMin, pb, prefer, history);
    if (s !== null) out.push({ day: dayKey(prev), start: s, end: s + durationMin, reason: `The day before. ${freeUntil(s + durationMin, pb)}` });
  }

  // Same day anyway: the time that overlaps busy blocks the least.
  let least: { s: number; over: number } | null = null;
  for (let s = firstStart(day, now); s + durationMin <= DAY_END_MIN; s += STEP_MIN) {
    const over = overlapMinutes({ start: s, end: s + durationMin }, busy);
    const better = !least || over < least.over || (over === least.over && prefer !== "any" && partOfDay(s) === prefer && partOfDay(least.s) !== prefer);
    if (better) least = { s, over };
  }
  if (least) {
    out.push({
      day: dayKey(day),
      start: least.s,
      end: least.s + durationMin,
      tag: "overlaps",
      reason: `Same day. Overlaps a busy time by ${least.over} minutes.`,
    });
  }
  return out;
}

/** Without free/busy access: three common times, not checked against the calendar. */
export function commonSlots(day: Date, durationMin: number, prefer: TimeOfDay, history: History, now: Date): Slot[] {
  const morning = history.part === "morning" && history.typicalMin !== null ? history.typicalMin : 8 * 60;
  const options = [
    { start: morning, reason: `Morning${history.part === "morning" && history.count >= 2 ? `, like your last ${history.count} completed steps` : ""}.` },
    { start: 12 * 60 + 30, reason: "Around lunch." },
    { start: 19 * 60 + 30, reason: "Evening." },
  ];
  const from = firstStart(day, now);
  const slots = options
    .filter((o) => o.start >= from && o.start + durationMin <= DAY_END_MIN + 60)
    .map((o) => ({ day: dayKey(day), start: o.start, end: o.start + durationMin, reason: o.reason }));
  const preferred = slots.find((s) => prefer !== "any" && partOfDay(s.start) === prefer) ?? slots[0];
  return preferred ? [preferred, ...slots.filter((s) => s !== preferred)] : [];
}

/** A typed time: what it clashes with, and the next free start after the clash. */
export function checkOwnTime(
  start: number,
  durationMin: number,
  busy: Interval[],
): { clash: Interval | null; nextFree: number | null } {
  const clash = overlapping({ start, end: start + durationMin }, busy);
  if (!clash) return { clash: null, nextFree: null };
  for (let s = clash.end; s + durationMin <= DAY_END_MIN; s += STEP_MIN) {
    if (!overlapping({ start: s, end: s + durationMin }, busy)) return { clash, nextFree: s };
  }
  return { clash, nextFree: null };
}

/**
 * When this person tends to finish steps: the part of the day most of their recent
 * completed, scheduled steps started in, and their usual time within it.
 */
export function historyFrom(completedStarts: Date[], recent = 5): History {
  const latest = [...completedStarts].sort((a, b) => b.getTime() - a.getTime()).slice(0, recent);
  const byPart = new Map<Exclude<TimeOfDay, "any">, number[]>();
  for (const d of latest) {
    const m = minutesOf(d);
    const part = partOfDay(m);
    byPart.set(part, [...(byPart.get(part) ?? []), m]);
  }
  let top: [Exclude<TimeOfDay, "any">, number[]] | null = null;
  for (const entry of byPart) if (!top || entry[1].length > top[1].length) top = entry;
  if (!top || top[1].length < 2) return NO_HISTORY;
  const sorted = [...top[1]].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  return { part: top[0], count: top[1].length, typicalMin: Math.round(median / STEP_MIN) * STEP_MIN };
}

// ── Rhythms ──────────────────────────────────────────────────────────────────

export interface RhythmDate {
  day: string;
  /** The rhythm's time on this date. */
  planned: number;
  /** When the event actually goes: `planned`, or the next free time if it moved. */
  start: number;
  status: "free" | "moved" | "skipped" | "overlaps" | "unchecked";
  /** Skipped by the person in review, rather than by the busy rule. */
  byPerson?: boolean;
}

/** The calendar days a rhythm lands on, from its start date for its weeks (8 when ongoing). */
export function rhythmDays(r: Pick<Rhythm, "frequency" | "days" | "startDate" | "weeks">): Date[] {
  const start = parseDayKey(r.startDate);
  const total = (r.weeks ?? CHECK_WEEKS) * 7;
  const out: Date[] = [];
  for (let offset = 0; offset < total; offset++) {
    const d = addDays(start, offset);
    if (!r.days.includes(d.getDay() as Weekday)) continue;
    if (r.frequency === "biweekly" && Math.floor(offset / 7) % 2 === 1) continue;
    out.push(d);
  }
  return out;
}

function movedStart(planned: number, durationMin: number, busy: Interval[], clash: Interval): number | null {
  const from = Math.ceil((clash.end + BUFFER_MIN) / STEP_MIN) * STEP_MIN;
  for (let s = Math.max(from, planned); s + durationMin <= DAY_END_MIN; s += STEP_MIN) {
    if (!overlapping({ start: s, end: s + durationMin }, busy)) return s;
  }
  return null;
}

/**
 * Every date of a rhythm, checked against busy times by the person's rule. `busyFor`
 * returns null when free/busy isn't available (dates are then "unchecked"); dates the
 * person tapped to skip in review stay skipped.
 */
export function checkRhythm(
  r: Rhythm,
  busyFor: (day: Date) => Interval[] | null,
  now: Date,
  personSkips: ReadonlySet<string> = new Set(),
): RhythmDate[] {
  return rhythmDays(r)
    .filter((d) => !(sameDay(d, now) && r.minutes <= minutesOf(now)) && d >= startOfDay(now))
    .map((d): RhythmDate => {
      const key = dayKey(d);
      const base = { day: key, planned: r.minutes, start: r.minutes };
      if (personSkips.has(key)) return { ...base, status: "skipped", byPerson: true };
      const busy = busyFor(d);
      if (!busy) return { ...base, status: "unchecked" };
      const clash = overlapping({ start: r.minutes, end: r.minutes + r.durationMin }, busy);
      if (!clash) return { ...base, status: "free" };
      if (r.ifBusy === "keep") return { ...base, status: "overlaps" };
      if (r.ifBusy === "move") {
        const moved = movedStart(r.minutes, r.durationMin, busy, clash);
        if (moved !== null) return { ...base, start: moved, status: "moved" };
      }
      return { ...base, status: "skipped" };
    });
}

/** The dates that become events (everything not skipped). */
export function datesToAdd(dates: RhythmDate[]): RhythmDate[] {
  return dates.filter((d) => d.status !== "skipped");
}

/** "15 free · 1 moved" */
export function rhythmTally(dates: RhythmDate[]): string {
  const count = (s: RhythmDate["status"]) => dates.filter((d) => d.status === s).length;
  const parts = [`${count("free")} free`];
  if (count("moved")) parts.push(`${count("moved")} moved`);
  if (count("skipped")) parts.push(`${count("skipped")} skipped`);
  if (count("overlaps")) parts.push(`${count("overlaps")} overlap${count("overlaps") === 1 ? "s" : ""}`);
  return parts.join(" · ");
}

/** For each weekday, how many of its dates are free at `minutes` ("6/8"). */
export function weekdayFit(
  r: Pick<Rhythm, "frequency" | "startDate" | "weeks" | "durationMin">,
  minutes: number,
  busyFor: (day: Date) => Interval[] | null,
  now: Date,
): Record<Weekday, { free: number; total: number } | null> {
  const out = {} as Record<Weekday, { free: number; total: number } | null>;
  for (let w = 0; w < 7; w++) {
    const days = rhythmDays({ ...r, days: [w as Weekday] }).filter((d) => d >= startOfDay(now));
    let free = 0;
    let known = true;
    for (const d of days) {
      const busy = busyFor(d);
      if (!busy) {
        known = false;
        break;
      }
      if (!overlapping({ start: minutes, end: minutes + r.durationMin }, busy)) free++;
    }
    out[w as Weekday] = known ? { free, total: days.length } : null;
  }
  return out;
}

export interface RhythmSuggestion {
  days: Weekday[];
  minutes: number;
  free: number;
  total: number;
  reason: string;
}

function consequence(missed: number, ifBusy: IfBusy): string {
  if (missed === 0) return "";
  const one = missed === 1;
  if (ifBusy === "move") return one ? " One date moves to the next free time." : ` ${missed} dates move to the next free time.`;
  if (ifBusy === "skip") return one ? " One date is skipped." : ` ${missed} dates are skipped.`;
  return one ? " One date overlaps a busy time." : ` ${missed} dates overlap a busy time.`;
}

/**
 * Suggested rhythms, checked against the next weeks: two non-adjacent days a week
 * (weekly), one day every other week (biweekly), or every day / weekdays (daily), at
 * times in the part of the day the person picked.
 */
export function suggestRhythms(input: {
  frequency: Rhythm["frequency"];
  durationMin: number;
  startDate: string;
  weeks: number | null;
  ifBusy: IfBusy;
  prefer: TimeOfDay;
  history: History;
  busyFor: (day: Date) => Interval[] | null;
  now: Date;
}): RhythmSuggestion[] {
  const { frequency, durationMin, startDate, weeks, ifBusy, prefer, history, busyFor, now } = input;
  const range = prefer === "morning" ? [7 * 60, 11 * 60] : prefer === "afternoon" ? [12 * 60, 16 * 60] : prefer === "evening" ? [17 * 60, 20 * 60] : [7 * 60, 20 * 60];
  const times = new Set<number>();
  for (let m = range[0]; m <= range[1]; m += 30) times.add(m);
  if (history.typicalMin !== null && (prefer === "any" || history.part === prefer)) times.add(history.typicalMin);
  const anchor = preferredAnchor(prefer, history);

  const fit = (days: Weekday[], minutes: number) => {
    const all = checkRhythm({ frequency, days, minutes, durationMin, startDate, weeks, ifBusy }, busyFor, now);
    const checked = all.filter((d) => d.status !== "unchecked");
    return { free: checked.filter((d) => d.status === "free").length, total: all.length, known: checked.length === all.length };
  };

  let combos: Weekday[][];
  if (frequency === "daily") combos = [[0, 1, 2, 3, 4, 5, 6], [1, 2, 3, 4, 5]];
  else if (frequency === "biweekly") combos = [1, 2, 3, 4, 5, 6, 0].map((d) => [d as Weekday]);
  else {
    combos = [];
    for (let a = 0; a < 7; a++)
      for (let b = a + 2; b < 7; b++) if (!(a === 0 && b === 6)) combos.push([a as Weekday, b as Weekday]);
  }

  const scored = combos.flatMap((days) =>
    [...times].map((minutes) => {
      const f = fit(days, minutes);
      const ratio = f.total ? f.free / f.total : 0;
      const weekdayBonus = days.every((d) => d >= 1 && d <= 5) ? 0.5 : 0;
      return { days, minutes, ...f, score: (f.known ? ratio * 100 : 0) - Math.abs(minutes - anchor) / 15 + weekdayBonus };
    }),
  );
  scored.sort((a, b) => b.score - a.score);

  const picked: typeof scored = [];
  for (const s of scored) {
    if (picked.length >= 3) break;
    const sameDays = picked.some((p) => p.days.join() === s.days.join());
    if (frequency === "daily" ? picked.some((p) => p.days.join() === s.days.join() && p.minutes === s.minutes) : sameDays) continue;
    picked.push(s);
  }

  return picked.map((p) => {
    let reason: string;
    if (!p.known) reason = "Not checked against your calendar.";
    else if (p.free === p.total) {
      const weekend = p.days.some((d) => d === 0 || d === 6) && p.days.length < 7;
      reason = `Free on all ${p.total} dates.${weekend ? " The weekend gives a calmer start." : ""}`;
    } else reason = `Free on ${p.free} of ${p.total} dates.${consequence(p.total - p.free, ifBusy)}`;
    return { days: p.days, minutes: p.minutes, free: p.free, total: p.total, reason };
  });
}
